// (c) chaehongee. Confidential and proprietary.

import { useProgress } from '@react-three/drei'
import { listAiModelsCached } from '../../services/ai_models'
import { subscribeToNewsletter } from '../../services/newsletter'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type ReactElement,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react'
import { SPHERE_CONFIG, SCROLL_CONFIG } from './constants'
import styles from './Landing.module.css'
import { HeroGLBScene, FooterGLBScene } from './Landing3D'
import PrimaryButton from '../PrimaryButton/PrimaryButton'
import { LoginPage } from '../Auth/LoginPage'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { useAuth } from '../../store/hooks/useAuth'

type LandingProps = {
  onStart?: () => void
}

type Vec2 = { x: number; y: number }

const DEFAULT_HERO_ANCHOR: Vec2 = { x: 0.5, y: 0.5 }

const HERO_GLB_SRC = '/aetherstudio.glb'
const PRODUCT_PREVIEW_IMAGE = '/images/example.png'

type ProviderModel = {
  name: string
  descriptionKey: string
}

type ProviderCard = {
  providerKey: string
  logoSrc: string
  bgSrc?: string
  bgVideo?: string
  models: ProviderModel[]
  size: 'large' | 'tall' | 'medium'
}

// Grid layout (3 cols, Tetris-packed):
// Row 1: [Google  span2]              [Higgsfield span1 tall]
// Row 2: [BFL     span1] [Qwen span1] [Higgsfield cont'd  ]
// Row 3: [Kling   span2]              [Runway     span1   ]
// Row 4: [Topaz   span1] [LoRA span1] [+ more    span1   ]
const PROVIDER_CARDS: ProviderCard[] = [
  {
    providerKey: 'providerGoogle',
    logoSrc: '/images/providers/google.svg',
    bgSrc: '/images/providers/google-bg.jpg',
    size: 'large',
    models: [
      { name: 'Imagen 4', descriptionKey: 'modelImagen4' },
      { name: 'Gemini 2.5 Flash', descriptionKey: 'modelGemini' },
    ],
  },
  {
    providerKey: 'providerHiggsfield',
    logoSrc: '/images/providers/higgsfield.svg',
    bgSrc: '/images/providers/higgsfield-bg.webp',
    size: 'tall',
    models: [
      { name: 'Soul', descriptionKey: 'modelSoul' },
      { name: 'Character Lock', descriptionKey: 'modelCharacterLock' },
    ],
  },
  {
    providerKey: 'providerBFL',
    logoSrc: '/images/providers/bfl.svg',
    bgVideo: '/images/providers/bfl-bg.mp4',
    size: 'medium',
    models: [
      { name: 'FLUX Kontext Max', descriptionKey: 'modelFluxKontext' },
      { name: 'FLUX Pro', descriptionKey: 'modelFluxPro' },
    ],
  },
  {
    providerKey: 'providerQwen',
    logoSrc: '/images/providers/qwen.svg',
    bgVideo: '/images/providers/qwen-bg.mp4',
    size: 'medium',
    models: [
      { name: 'Image Edit', descriptionKey: 'modelImageEdit' },
      { name: 'Multi-Angle', descriptionKey: 'modelMultiAngle' },
    ],
  },
  {
    providerKey: 'providerKling',
    logoSrc: '/images/providers/kling.svg',
    bgVideo: '/images/providers/kling-bg.mp4',
    size: 'large',
    models: [
      { name: 'Video v2.5 Turbo', descriptionKey: 'modelVideoTurbo' },
      { name: 'Interpolation', descriptionKey: 'modelInterpolation' },
    ],
  },
  {
    providerKey: 'providerRunway',
    logoSrc: '/images/providers/runway.svg',
    bgSrc: '/images/providers/runway-bg.png',
    size: 'medium',
    models: [
      { name: 'Gen-4', descriptionKey: 'modelGen4' },
    ],
  },
  {
    providerKey: 'providerTopaz',
    logoSrc: '/images/providers/topaz.svg',
    bgSrc: '/images/providers/topaz-bg.png',
    size: 'medium',
    models: [
      { name: 'AI Upscale', descriptionKey: 'modelUpscale' },
    ],
  },
  {
    providerKey: 'providerLoRA',
    logoSrc: '/images/providers/lora.svg',
    bgSrc: '/images/providers/lora-bg.png',
    size: 'medium',
    models: [
      { name: 'Fine-tuned Models', descriptionKey: 'modelFineTuned' },
    ],
  },
  {
    providerKey: 'providerMore',
    logoSrc: '',
    bgSrc: '/images/providers/more-bg.jpg',
    size: 'medium',
    models: [],
  },
]

function ProviderLogo({ logoSrc, provider }: { logoSrc: string; provider: string }): ReactElement {
  if (!logoSrc) {
    return <div className={styles.providerLogoText}>{provider}</div>
  }
  return (
    <div className={styles.providerLogo}>
      <img src={logoSrc} alt={provider} className={styles.logoImg} />
    </div>
  )
}
const PRERENDER_EVENT_NAME = 'prerender-ready'

export function Landing(props: LandingProps): ReactElement {
  if (isPrerendering()) {
    return <LandingStatic />
  }

  return <LandingInteractive {...props} />
}

function LandingStatic(): ReactElement {
  const { t } = useTranslation('landing')
  const tc = useTranslation('common').t

  useEffect(() => {
    setTimeout(() => {
      document.dispatchEvent(new Event(PRERENDER_EVENT_NAME))
    }, 0)
  }, [])

  return (
    <div className={`theme-light ${styles.root}`} data-prerender="true">
      <header className={styles.siteHeader}>
        <div className={styles.maxWidth}>
          <div className={styles.siteHeaderInner}>
            <div className={styles.brandMark}>
              <span className={styles.brandDot} aria-hidden="true" />
            </div>
            <nav className={styles.heroNav}>
              <a className={styles.minLink} href="/blog">
                {tc('blog')}
              </a>
              <button className={styles.minLink} onClick={navigateToSessions}>
                {tc('explore')}
              </button>
            </nav>
            <div className={styles.heroActions}>
              <button className={styles.minLink} onClick={navigateToSessions}>
                <span className={styles.signUpLink}>{t('auth:joinTheBeta')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroChrome}>
          <div className={styles.maxWidth}>
            <h1 className={styles.heroTitle}>{t('heroTitle')}</h1>
            <p className={styles.heroSubcopy}>
              {t('heroSubcopy')}
            </p>
          </div>
        </div>
      </section>

      <section className={styles.featureSection}>
        <div className={styles.maxWidth}>
          <div className={styles.featureGrid}>
            <div className={styles.previewCol}>
              <div className={styles.preview}>
                <div className={styles.previewTopBar}>
                  <div className={styles.previewHeaderLeft}>
                    <div className={styles.previewDots}>
                      <div className={styles.dot} style={{ backgroundColor: '#ed6a5e' }} />
                      <div className={styles.dot} style={{ backgroundColor: '#f5bd4f' }} />
                      <div className={styles.dot} style={{ backgroundColor: '#61c454' }} />
                    </div>
                    <div className={styles.previewTitle}>{tc('session')}</div>
                  </div>
                  <div className={styles.previewPills}>
                    <span className={styles.pill}>{tc('mask')}</span>
                    <span className={styles.pill}>{tc('compose')}</span>
                    <span className={styles.pill}>{tc('direct')}</span>
                  </div>
                </div>
                <div className={styles.previewBody}>
                  <div className={styles.previewSidebar}>
                    <div className={styles.sidebarItemStrong}>{tc('canvas')}</div>
                    <div className={styles.sidebarItem}>{tc('history')}</div>
                    <div className={styles.sidebarItem}>{tc('tools')}</div>
                    <div className={styles.sidebarDivider} />
                    <div className={styles.sidebarSmall}>{tc('agentPicksTool')}</div>
                  </div>
                  <div className={styles.previewCanvasArea}>
                    <div className={styles.previewCanvasSurface}>
                      <img
                        className={styles.previewImage}
                        src={PRODUCT_PREVIEW_IMAGE}
                        alt={t('productPreviewAlt')}
                      />
                      <div className={styles.previewOverlay} aria-hidden="true" />
                    </div>
                    <div className={styles.previewBottomRow}>
                      <div className={styles.previewHint}>{tc('dragToCompose')}</div>
                      <PrimaryButton onClick={navigateToSessions}>{tc('open')}</PrimaryButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.featureCopy}>
              <h2 className={styles.sectionTitle}>{t('sectionTitle')}</h2>
              <div className={styles.featureRotatingWord}>
                <span className={styles.rotatingWord}>
                  {(t('rotatingWords', { returnObjects: true }) as string[])[3]}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.modelsShowcase}>
        <div className={styles.modelsShowcaseInner}>
          <div className={styles.modelsHeader}>
            <h2 className={styles.modelsTitle}>Every model. One studio.</h2>
            <p className={styles.modelsSubtitle}>
              Your agent picks the right model for each task. No API keys, no switching tools.
            </p>
          </div>
          <div className={styles.modelsGrid}>
            {PROVIDER_CARDS.map((card) => (
              <div
                key={card.providerKey}
                className={`${styles.providerCard} ${card.size === 'large'
                  ? styles.providerCardLarge
                  : card.size === 'medium'
                    ? styles.providerCardMedium
                    : styles.providerCardSmall
                  } ${card.bgSrc || card.bgVideo ? styles.providerCardWithBg : ''}`}
              >
                {card.bgVideo && (
                  <video
                    src={card.bgVideo}
                    className={styles.providerCardBg}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                )}
                {card.bgSrc && !card.bgVideo && (
                  <img
                    src={card.bgSrc}
                    alt=""
                    className={styles.providerCardBg}
                    loading="lazy"
                  />
                )}
                {(card.bgSrc || card.bgVideo) && <div className={styles.providerCardGradient} />}
                <div className={`${styles.providerCardContent} ${card.models.length === 0 ? styles.providerCardCenterContent : ''}`}>
                  <ProviderLogo logoSrc={card.logoSrc} provider={t(card.providerKey)} />
                  {card.models.length > 0 && (
                    <div className={styles.providerModels}>
                      {card.models.map((model) => (
                        <div key={model.name} className={styles.modelItem}>
                          <div className={styles.modelName}>{model.name}</div>
                          <div className={styles.modelDesc}>{t(model.descriptionKey)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.providerCardShine} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

function LandingInteractive(_props: LandingProps): ReactElement {
  const { t } = useTranslation('landing')
  const tc = useTranslation('common').t
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { user } = useAuth()

  // Newsletter form state
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  // Parallax refs
  const heroCanvasRef = useRef<HTMLDivElement>(null)
  const heroChromeRef = useRef<HTMLDivElement>(null)

  // Force body background to match landing page's forced-light theme
  useEffect(() => {
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#ffffff'
    return () => { document.body.style.backgroundColor = prev }
  }, [])

  // Parallax scroll effect
  useEffect(() => {
    let raf: number
    const onScroll = () => {
      raf = requestAnimationFrame(() => {
        const y = window.scrollY
        if (heroCanvasRef.current) {
          heroCanvasRef.current.style.transform = `translateY(${y * 0.35}px)`
        }
        if (heroChromeRef.current) {
          heroChromeRef.current.style.transform = `translateY(${y * 0.15}px)`
        }
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [])

  const rotatingWords = t('rotatingWords', { returnObjects: true }) as string[]
  const wordIndex = useRotatingWordIndex(rotatingWords, 1800)
  const { isLoading: isFetchingModels } = useModelImageUrls()
  const { active, progress: threeProgress } = useProgress()
  const [minTimePassed, setMinTimePassed] = useState(false)
  const [displayProgress, setDisplayProgress] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const [fallbackProgress, setFallbackProgress] = useState(0)
  const [modelsFetchCompleteTime, setModelsFetchCompleteTime] = useState<number | null>(null)
  const [loadingBarState, setLoadingBarState] = useState<'hidden' | 'loading' | 'completing'>('hidden')

  useEffect(() => {
    const t = setTimeout(() => setMinTimePassed(true), 1500)
    return () => clearTimeout(t)
  }, [])

  // Track when models fetch completes
  useEffect(() => {
    if (!isFetchingModels && modelsFetchCompleteTime === null) {
      setModelsFetchCompleteTime(Date.now())
    }
  }, [isFetchingModels, modelsFetchCompleteTime])

  // Fallback: if useProgress isn't tracking, estimate progress based on time since models completed
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let intervalId: ReturnType<typeof setInterval> | null = null

    if (!isFetchingModels && !active && threeProgress === 0 && modelsFetchCompleteTime !== null) {
      // Models are done, but 3D progress is stuck at 0 and not active
      // Wait a bit to see if useProgress starts tracking, then use fallback
      timeoutId = setTimeout(() => {
        intervalId = setInterval(() => {
          // Check if useProgress started working (this will be checked via effect re-run)
          setFallbackProgress(() => {
            const elapsed = Date.now() - modelsFetchCompleteTime!
            const estimated = Math.min(100, (elapsed / 3000) * 100) // Assume ~3s for 3D loading
            return estimated
          })
        }, 100)
      }, 800) // Wait 800ms before starting fallback
    } else {
      // useProgress is working or models still fetching, reset fallback
      setFallbackProgress(0)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [isFetchingModels, active, threeProgress, modelsFetchCompleteTime])

  useEffect(() => {
    let target = 0
    if (isFetchingModels) {
      // While fetching list: crawl up to 30%
      target = 30
    } else {
      // Fetch done: map 3D progress (0..100) to remaining 70%
      // Use threeProgress if it's working, otherwise use time-based fallback
      const effectiveProgress = active || threeProgress > 0 ? threeProgress : fallbackProgress
      target = 30 + (effectiveProgress * 0.7)
    }

    // However, if everything is truly done, jump to 100
    if (!isFetchingModels && !active && threeProgress === 100) {
      target = 100
    }

    // Smoothly animate displayProgress towards target
    const interval = setInterval(() => {
      setDisplayProgress((prev) => {
        if (prev >= target) return prev
        const dist = target - prev
        // Move faster if we're further away
        const inc = Math.max(0.5, dist * 0.1)
        return Math.min(target, prev + inc)
      })
    }, 16)

    return () => clearInterval(interval)
  }, [isFetchingModels, active, threeProgress, fallbackProgress])

  // Completion check
  useEffect(() => {
    // If displayProgress is done, we are ready (covers both real loading and fallback)
    if (minTimePassed && !isFetchingModels && !active && displayProgress >= 99) {
      setIsReady(true)
    }
  }, [minTimePassed, isFetchingModels, active, displayProgress])

  // Sync loading bar state with isReady
  useEffect(() => {
    if (!isReady) {
      setLoadingBarState('loading')
    } else if (loadingBarState === 'loading') {
      setLoadingBarState('completing')
      const timer = setTimeout(() => {
        setLoadingBarState('hidden')
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [isReady, loadingBarState])

  useModelsScrollProgress()

  async function handleNewsletterSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()

    if (!email.trim()) {
      setSubmitStatus('error')
      setSubmitMessage(t('enterEmailError'))
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')

    try {
      const response = await subscribeToNewsletter({
        email: email.trim(),
        source: 'landing_page',
        tags: ['early_access', 'landing_page']
      })

      setSubmitStatus('success')
      setSubmitMessage(response.message || t('subscribeSuccess'))
      setEmail('') // Clear the form

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle')
        setSubmitMessage('')
      }, 5000)

    } catch (error) {
      console.error('Newsletter subscription error:', error)
      setSubmitStatus('error')
      setSubmitMessage(
        error instanceof Error
          ? error.message
          : t('subscribeFailed')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`theme-light ${styles.root}`}>
      {loadingBarState !== 'hidden' && (
        <div className={styles.topLoadingBar}>
          <div
            className={`${styles.topLoadingBarFill} ${loadingBarState === 'completing' ? styles.topLoadingBarComplete : ''}`}
            style={{ width: `${displayProgress}%` }}
          ></div>
        </div>
      )}
      {!isReady && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingText}>{Math.floor(displayProgress)}%</div>
        </div>
      )}
      <header className={styles.siteHeader}>
        <div className={styles.maxWidth}>
          <div className={styles.siteHeaderInner}>
            <div className={styles.brandMark}>
              <span className={styles.brandDot} aria-hidden="true" />
            </div>

            <nav className={styles.heroNav}>
              <a className={styles.minLink} href="/blog">
                {tc('blog')}
              </a>
              <button className={styles.minLink} onClick={navigateToSessions}>
                {tc('explore')}
              </button>
            </nav>

            <div className={styles.heroActions}>
              <button
                className={styles.minLink}
                onClick={() => {
                  if (user) {
                    navigateToSessions()
                  } else {
                    setShowLoginModal(true)
                  }
                }}
              >
                <span className={styles.signUpLink}>{t('auth:joinTheBeta')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className={styles.hero}>
        <div ref={heroCanvasRef} className={styles.heroCanvas} aria-hidden="true">
          <HeroGLBScene src={HERO_GLB_SRC} />
        </div>

        <div ref={heroChromeRef} className={styles.heroChrome}>
          <div className={styles.maxWidth}>
            <h1 className={styles.heroTitle}>
              {t('heroTitle')}
            </h1>

            <p className={styles.heroSubcopy}>
              {t('heroSubcopy')}
            </p>
          </div>
        </div>
      </section>

      <section className={styles.featureSection}>
        <div className={styles.maxWidth}>
          <div className={styles.featureGrid}>
            <ProductPreview onStart={() => setShowLoginModal(true)} isSticky />
            <div className={styles.featureCopy}>
              {t('sectionLayout') === 'wordFirst' ? (
                <>
                  <div className={styles.featureRotatingWord}>
                    <HighlightedWord key={wordIndex} word={rotatingWords[wordIndex]} />
                  </div>
                  <Reveal threshold={0.2}>
                    <h2 className={styles.sectionTitle}>{t('sectionTitle')}</h2>
                  </Reveal>
                </>
              ) : (
                <>
                  <Reveal threshold={0.2}>
                    <h2 className={styles.sectionTitle}>{t('sectionTitle')}</h2>
                  </Reveal>
                  <div className={styles.featureRotatingWord}>
                    <HighlightedWord key={wordIndex} word={rotatingWords[wordIndex]} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <ModelsShowcase onSignUp={() => setShowLoginModal(true)} />

      <footer className={styles.footer}>
        <div className={styles.maxWidth}>
          <div className={styles.footerMain}>
            <div className={styles.footerTop}>
              <LazyFooterCanvas src={HERO_GLB_SRC} />
              <button className={styles.enterStudioBtn} onClick={navigateToSessions}>
                <span className={styles.enterStudioBtnText}>{t('reserveYourSpot')}<br /></span>
                {t('enterStudio')}
              </button>
            </div>

            <div className={styles.footerMiddle}>
              <div className={styles.footerNewsletter}>
                <div className={styles.footerLabel} style={{ marginLeft: '0.7rem' }}>{t('stayInTheLoop')}</div>
                <form onSubmit={handleNewsletterSubmit} className={styles.emailInputWrapper}>
                  <input
                    type="email"
                    placeholder={t('enterYourEmail')}
                    className={styles.emailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    aria-label={t('auth:emailAddress')}
                  />
                  <button
                    type="submit"
                    className={styles.subscribeBtn}
                    disabled={isSubmitting || submitStatus === 'success'}
                    aria-label={isSubmitting ? t('subscribing') : t('subscribe')}
                  >
                    {submitStatus === 'success' ? (
                      <CheckCircle size={20} />
                    ) : submitStatus === 'error' ? (
                      <AlertCircle size={20} />
                    ) : (
                      <ArrowRight size={20} />
                    )}
                  </button>
                </form>
                {submitMessage && (
                  <div
                    className={`${styles.submitMessage} ${submitStatus === 'success' ? styles.submitMessageSuccess :
                      submitStatus === 'error' ? styles.submitMessageError : ''
                      }`}
                    style={{ marginLeft: '0.7rem', marginTop: '0.5rem' }}
                  >
                    {submitMessage}
                  </div>
                )}
              </div>

              <div className={styles.footerLinksGroup}>
                <div className={styles.footerInfo}>
                  <div className={styles.footerLabel}>{tc('product')}</div>
                  <nav className={styles.footerNavColumn}>
                    <a href="/features/workflow" className={styles.footerLink}>{tc('features')}</a>
                    <a href="/blog" className={styles.footerLink}>{tc('blog')}</a>
                  </nav>
                </div>

                <div className={styles.footerInfo}>
                  <div className={styles.footerLabel}>{t('info')}</div>
                  <nav className={styles.footerNavColumn}>
                    <a href="/privacy" className={styles.footerLink}>{tc('privacyPolicy')}</a>
                    <a href="/terms" className={styles.footerLink}>{tc('termsOfService')}</a>
                  </nav>
                </div>

              </div>

              <div className={styles.footerInfo}>
                <div className={styles.footerLabel}>{tc('language')}</div>
                <LanguageSwitcher variant="compact" />
              </div>
            </div>

            <div className={styles.footerBrandBig}>
              {t('footerBrand')}
            </div>
          </div>
        </div>
      </footer>

      {/* Login modal overlay */}
      {showLoginModal && (
        <LoginPage
          onClose={() => setShowLoginModal(false)}
          returnUrl="/"
        />
      )}
    </div>
  )
}

function ModelsShowcase({ onSignUp }: { onSignUp: () => void }): ReactElement {
  const { t } = useTranslation('landing')
  const tc = useTranslation('common').t

  return (
    <section className={styles.modelsShowcase}>
      <div className={styles.modelsShowcaseInner}>
        <div className={styles.modelsHeader}>
          <Reveal threshold={0.15}>
            <h2 className={styles.modelsTitle}>
              {t('modelsTitle')}
            </h2>
          </Reveal>
          <Reveal threshold={0.15} delay={0.1}>
            <p className={styles.modelsSubtitle}>
              {t('modelsSubtitle')}
            </p>
          </Reveal>
        </div>

        <div className={styles.modelsGrid}>
          {PROVIDER_CARDS.map((card, i) => (
            <Reveal
              key={card.providerKey}
              className={`${styles.providerCard} ${card.size === 'large'
                ? styles.providerCardLarge
                : card.size === 'tall'
                  ? styles.providerCardTall
                  : styles.providerCardMedium
                } ${card.bgSrc || card.bgVideo ? styles.providerCardWithBg : ''}`}
              threshold={0.1}
              delay={0.08 * i}
            >
              {card.bgVideo && (
                <video
                  src={card.bgVideo}
                  className={styles.providerCardBg}
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              )}
              {card.bgSrc && !card.bgVideo && (
                <img
                  src={card.bgSrc}
                  alt=""
                  className={styles.providerCardBg}
                  loading="lazy"
                />
              )}
              {(card.bgSrc || card.bgVideo) && <div className={styles.providerCardGradient} />}
              <div className={`${styles.providerCardContent} ${card.models.length === 0 ? styles.providerCardCenterContent : ''}`}>
                <ProviderLogo logoSrc={card.logoSrc} provider={t(card.providerKey)} />
                {card.models.length > 0 && (
                  <div className={styles.providerModels}>
                    {card.models.map((model) => (
                      <div key={model.name} className={styles.modelItem}>
                        <div className={styles.modelName}>{model.name}</div>
                        <div className={styles.modelDesc}>{t(model.descriptionKey)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.providerCardShine} />
            </Reveal>
          ))}
        </div>

        <Reveal threshold={0.15} delay={0.2}>
          <div className={styles.modelsFooterCta}>
            <span className={styles.modelsCtaText}>
              {t('modelsCta')}
            </span>
            <div className={styles.modelsCtaButtons}>
              <button className={styles.modelsCtaSecondary} onClick={navigateToSessions}>
                {tc('explore')}
              </button>
              <button className={styles.modelsCtaPrimary} onClick={onSignUp}>
                {t('modelsCtaStart')}
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

type ProductPreviewProps = {
  onStart?: () => void
  isSticky?: boolean
}

function ProductPreview({ onStart: _onStart, isSticky = false }: ProductPreviewProps): ReactElement {
  const { t } = useTranslation('landing')
  const tc = useTranslation('common').t
  const ref = useRef<HTMLDivElement | null>(null)
  const [tilt, setTilt] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const onLeave = () => setTilt({ x: 0, y: 0 })

    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect()
      const px = (e.clientX - r.left) / Math.max(1, r.width)
      const py = (e.clientY - r.top) / Math.max(1, r.height)
      setTilt({ x: (px - 0.5) * 2, y: (py - 0.5) * 2 })
    }

    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseleave', onLeave)
    return () => {
      el.removeEventListener('mousemove', onMove)
      el.removeEventListener('mouseleave', onLeave)
    }
  }, [])

  return (
    <Reveal
      className={`${styles.previewCol} ${isSticky ? styles.previewColSticky : ''}`}
      threshold={0.15}
    >
      <div
        ref={ref}
        className={styles.preview}
        style={
          {
            '--tilt-x': String(tilt.x),
            '--tilt-y': String(tilt.y),
          } as CSSProperties
        }
      >
        <div className={styles.previewTopBar}>
          <div className={styles.previewHeaderLeft}>
            <div className={styles.previewDots}>
              <div className={styles.dot} style={{ backgroundColor: '#ed6a5e' }} />
              <div className={styles.dot} style={{ backgroundColor: '#f5bd4f' }} />
              <div className={styles.dot} style={{ backgroundColor: '#61c454' }} />
            </div>
            <div className={styles.previewTitle}>{tc('session')}</div>
          </div>
          <div className={styles.previewPills}>
            <span className={styles.pill}>{tc('mask')}</span>
            <span className={styles.pill}>{tc('compose')}</span>
            <span className={styles.pill}>{tc('direct')}</span>
          </div>
        </div>

        <div className={styles.previewBody}>
          <div className={styles.previewSidebar}>
            <div className={styles.sidebarItemStrong}>{tc('canvas')}</div>
            <div className={styles.sidebarItem}>{tc('history')}</div>
            <div className={styles.sidebarItem}>{tc('tools')}</div>
            <div className={styles.sidebarDivider} />
            <div className={styles.sidebarSmall}>{tc('agentPicksTool')}</div>
          </div>

          <div className={styles.previewCanvasArea}>
            <div className={styles.previewCanvasSurface}>
              <img
                className={styles.previewImage}
                src={PRODUCT_PREVIEW_IMAGE}
                alt={t('productPreviewAlt')}
              />
              <div className={styles.previewOverlay} aria-hidden="true" />
            </div>

            <div className={styles.previewBottomRow}>
              <div className={styles.previewHint}>{tc('dragToCompose')}</div>
              <PrimaryButton onClick={() => {
                window.location.href = 'https://aetherstudio.ai/session/a654269c-062e-4fbe-ad8f-364d00815685'
              }}>
                {tc('open')}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  )
}

type RevealProps = {
  children: React.ReactNode
  className?: string
  style?: CSSProperties
  threshold?: number
  delay?: number
}

function Reveal({ children, className, style, threshold = 0.1, delay = 0 }: RevealProps): ReactElement {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    )
    if (ref.current) {
      observer.observe(ref.current)
    }
    return () => observer.disconnect()
  }, [threshold])

  const combinedClassName = [
    className,
    isVisible ? styles.reveal : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  const combinedStyle: CSSProperties = {
    ...style,
    animationDelay: delay > 0 ? `${delay}s` : undefined,
    opacity: isVisible ? undefined : 0,
  }

  return (
    <div ref={ref} className={combinedClassName} style={combinedStyle}>
      {children}
    </div>
  )
}

type LineRect = { left: number; top: number; width: number; height: number }

function HighlightedWord({ word }: { word: string }): ReactElement {
  const textRef = useRef<HTMLSpanElement>(null)
  const [lines, setLines] = useState<LineRect[]>([])

  useEffect(() => {
    const el = textRef.current
    if (!el) return
    const raf = requestAnimationFrame(() => {
      const textNode = el.firstChild
      if (!textNode) return
      const range = document.createRange()
      range.selectNodeContents(el)
      const clientRects = Array.from(range.getClientRects())
      const parentRect = el.getBoundingClientRect()
      setLines(
        clientRects.map((r) => ({
          left: r.left - parentRect.left,
          top: r.top - parentRect.top,
          width: r.width,
          height: r.height,
        }))
      )
    })
    return () => cancelAnimationFrame(raf)
  }, [word])

  return (
    <span className={styles.rotatingWord} ref={textRef}>
      {lines.map((rect, i) => (
        <span
          key={i}
          className={styles.wordHighlight}
          style={{
            left: rect.left - 4,
            top: rect.top + rect.height * 0.38,
            width: rect.width + 8,
            height: rect.height * 0.62,
          }}
        />
      ))}
      {word}
    </span>
  )
}

function LazyFooterCanvas({ src }: { src: string }): ReactElement {
  const ref = useRef<HTMLDivElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { threshold: 0, rootMargin: '200px' } // Start loading 200px before footer enters viewport
    )
    if (ref.current) {
      observer.observe(ref.current)
    }
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref} className={styles.footerCanvas} aria-hidden="true">
      {shouldLoad && <FooterGLBScene src={src} />}
    </div>
  )
}

function useRotatingWordIndex(words: readonly string[], intervalMs: number): number {
  const [wordIndex, setWordIndex] = useState(0)

  useEffect(() => {
    if (words.length <= 1) return
    const interval = window.setInterval(() => {
      setWordIndex((i) => (i + 1) % words.length)
    }, intervalMs)
    return () => window.clearInterval(interval)
  }, [intervalMs, words])

  return wordIndex
}

function useModelsScrollProgress(): {
  modelsSectionRef: RefObject<HTMLElement | null>
  modelsScrollProgress: number
  scrollProgressRef: MutableRefObject<number>
  heroAnchorRef: MutableRefObject<Vec2>
  heroAnchor: Vec2
} {
  const modelsSectionRef = useRef<HTMLElement | null>(null)

  // Raw scroll progress (can jump on fast scroll).
  const rawScrollProgressRef = useRef(0)
  // Smoothed raw progress used for UI + 3D choreography (robust to fast scroll).
  const smoothedScrollProgressRef = useRef(0)
  // Remapped progress to slow the early animation phases (requires more scroll).
  const choreoScrollProgressRef = useRef(0)
  const lastPublishedChoreoRef = useRef(0)

  const [modelsScrollProgress, setModelsScrollProgress] = useState(0)

  const heroAnchorRef = useRef<Vec2>(DEFAULT_HERO_ANCHOR)
  const [heroAnchor, setHeroAnchor] = useState<Vec2>(DEFAULT_HERO_ANCHOR)

  useEffect(() => {
    const updateRawScrollProgress = () => {
      const el = modelsSectionRef.current
      if (!el) return

      const rect = el.getBoundingClientRect()
      const vh = window.innerHeight || 1

      // progress=0 when section top hits bottom of viewport
      // progress=1 when section bottom hits top of viewport
      const total = rect.height + vh
      const traveled = vh - rect.top
      rawScrollProgressRef.current = clamp01(traveled / total)

      // NOTE: state updates happen in the rAF loop (smoothed), not here.
    }

    updateRawScrollProgress()
    window.addEventListener('scroll', updateRawScrollProgress, { passive: true })
    window.addEventListener('resize', updateRawScrollProgress)
    return () => {
      window.removeEventListener('scroll', updateRawScrollProgress)
      window.removeEventListener('resize', updateRawScrollProgress)
    }
  }, [])

  useEffect(() => {
    let raf = 0
    let lastT = 0

    const tick = (t: number) => {
      // ~60fps; we avoid setState unless values actually change.
      const dt = Math.max(0.001, (t - (lastT || t)) / 1000)
      lastT = t

      // Smooth scroll progress so it doesn't jump on fast scrolling.
      const target = rawScrollProgressRef.current
      const current = smoothedScrollProgressRef.current
      const nextRaw = damp(current, target, SCROLL_CONFIG.smoothLambda, dt)
      smoothedScrollProgressRef.current = nextRaw

      // Slow down the early phases (solo + columns) so users must scroll more
      // to progress through the choreography. (1 maps to 1, but early values
      // grow more slowly.)
      const nextChoreo = scrollChoreo(nextRaw)
      choreoScrollProgressRef.current = nextChoreo

      const last = lastPublishedChoreoRef.current
      if (Math.abs(last - nextChoreo) > SCROLL_CONFIG.publishEpsilon) {
        lastPublishedChoreoRef.current = nextChoreo
        setModelsScrollProgress(nextChoreo)
      }

      const v = heroAnchorRef.current
      setHeroAnchor((prev) => {
        if (Math.abs(prev.x - v.x) < 0.001 && Math.abs(prev.y - v.y) < 0.001) return prev
        return { x: v.x, y: v.y }
      })

      raf = window.requestAnimationFrame(tick)
    }

    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [])

  return {
    modelsSectionRef,
    modelsScrollProgress,
    scrollProgressRef: choreoScrollProgressRef,
    heroAnchorRef,
    heroAnchor,
  }
}


function useModelImageUrls(): { imageUrls: string[]; isLoading: boolean } {
  const [state, setState] = useState({ imageUrls: [] as string[], isLoading: true })

  useEffect(() => {
    async function fetchModels() {
      try {
        const response = await listAiModelsCached({ page_size: 50 })
        const urls = response.items
          .map((item) => item.headshot_url)
          .filter((url): url is string => !!url)
        setState({ imageUrls: urls, isLoading: false })
      } catch (error) {
        console.error('Failed to fetch AI models', error)
        setState({ imageUrls: [], isLoading: false })
      }
    }

    void fetchModels()
  }, [])

  return state
}

function clamp01(v: number): number {
  if (v < 0) return 0
  if (v > 1) return 1
  return v
}

function damp(current: number, target: number, lambda: number, dtSeconds: number): number {
  // Exponential smoothing: stable across frame rates.
  return current + (target - current) * (1 - Math.exp(-lambda * dtSeconds))
}

function scrollChoreo(p: number): number {
  // Remap scroll progress so:
  // - Early phases (solo + columns) require more scroll (slower progression).
  // - Once the sphere is formed, we linger on the sphere for longer.
  //
  // This works by mapping the raw progress into a piecewise curve where we
  // reach "sphere complete" (q≈0.7) later, then advance q from 0.7→1 with a
  // high gamma (slow near 0, fast only near the very end).
  const raw = clamp01(p)
  const sphereCompleteQ = SPHERE_CONFIG.completeAt
  const sphereHoldStartP = SPHERE_CONFIG.holdStartRaw

  if (raw <= sphereHoldStartP) {
    const u = raw / Math.max(1e-6, sphereHoldStartP)
    const gammaEarly = 1.35
    return sphereCompleteQ * Math.pow(clamp01(u), gammaEarly)
  }

  const u = (raw - sphereHoldStartP) / Math.max(1e-6, 1 - sphereHoldStartP)
  const gammaLate = 2.4
  return sphereCompleteQ + (1 - sphereCompleteQ) * Math.pow(clamp01(u), gammaLate)
}

function isPrerendering(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as Window & { __PRERENDER__?: boolean }
  if (w.__PRERENDER__) return true
  if (window.location.search.includes('prerender=1')) return true
  return /HeadlessChrome/.test(navigator.userAgent)
}

function navigateToSessions(): void {
  window.location.href = '/sessions'
}
